import type {
  CollaborationHighlightEvent,
  CollaborationHighlightTargetType,
} from '@/stores/collaborationHighlightStore';

const CANVAS_ENTITY_HIGHLIGHT_ITEM_TYPES = {
  resource_pin: 'resource_pin',
  '15': 'resource_pin',
  storyboard_temporary_video: 'temporary_video',
  '21': 'temporary_video',
} as const satisfies Record<string, CollaborationHighlightTargetType>;

type CanvasEntityHighlightItemType = keyof typeof CANVAS_ENTITY_HIGHLIGHT_ITEM_TYPES;

export function normalizeCanvasEntityChangedItemType(itemType: unknown): string | null {
  if (typeof itemType === 'number' && Number.isFinite(itemType)) {
    return String(itemType);
  }
  if (typeof itemType === 'string' && itemType.trim()) {
    return itemType.trim();
  }
  return null;
}

function isCanvasEntityHighlightItemType(
  itemType: string
): itemType is CanvasEntityHighlightItemType {
  return itemType in CANVAS_ENTITY_HIGHLIGHT_ITEM_TYPES;
}

export function mapCanvasEntitiesChangedToHighlights(params: {
  projectId: string;
  storyboardId?: string | null;
  contextType: string;
  changedNodes: Array<{ operation: string; itemType: string | number; itemId: string }>;
  actorUserId?: string | null;
  occurredAt?: string | null;
  eventIdPrefix?: string | null;
}): CollaborationHighlightEvent[] {
  const occurredAt = params.occurredAt?.trim() || new Date().toISOString();
  const actorUserId = params.actorUserId ?? '';
  const eventIdPrefix = params.eventIdPrefix?.trim() || null;

  return params.changedNodes.flatMap((node) => {
    const itemType = normalizeCanvasEntityChangedItemType(node.itemType);
    if (node.operation !== 'created' || !itemType || !isCanvasEntityHighlightItemType(itemType)) {
      return [];
    }

    const targetType = CANVAS_ENTITY_HIGHLIGHT_ITEM_TYPES[itemType];
    const eventId =
      eventIdPrefix != null
        ? `${eventIdPrefix}:${targetType}:${node.itemId}`
        : `${params.projectId}:canvas:${targetType}:${node.itemId}:${occurredAt}`;

    const highlight: CollaborationHighlightEvent = {
      eventId,
      projectId: params.projectId,
      context: 'canvas',
      targetType,
      targetId: node.itemId,
      highlightType: 'new',
      changeType: 'created',
      actor: { userId: actorUserId },
      occurredAt,
    };

    if (params.storyboardId) {
      highlight.targetStoryboardId = params.storyboardId;
    }

    return [highlight];
  });
}
